import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Platform } from 'react-native';
import { JoinEntryCardProps, JoinEntryContainerProps } from './types';
import { RootState } from '../../../../store/models';
import { openExternalUrl } from '../../../../modules/openExternalUrl';
import { addQueryStringParams } from '../../../../modules/url';
import { JoinEntryCardTrackName, JoinEntryCardType } from './constants';
import { JoinEntry } from './JoinEntry';
import analytics from '../../../../modules/analytics';
import nonCriticalException from '../../../../modules/exceptionLogger';
import { SITE_ROOT } from '../../../../config';
import { getAndroidVersion, getIOSVersion } from '../../../../modules/AppInfo';

export const JoinEntryContainer = ({
  testID = 'join-entry-component',
}: JoinEntryContainerProps) => {
  const utmParams = useSelector((state: RootState) => state.route.params);
  const isApp = (!!getIOSVersion() || getAndroidVersion()) && window.appAvailability;

  const handleClicked = useCallback(async (cardType: JoinEntryCardType) => {
    try {
      await analytics.track(JoinEntryCardTrackName[cardType], {
        platform: Platform.OS,
      });
    } catch (e) {
      nonCriticalException(e);
    }

    const url = cardType === JoinEntryCardType.ListYourBusiness ? `${SITE_ROOT}/join` : `${SITE_ROOT}/join/${cardType}`;
    openExternalUrl(addQueryStringParams(url, {
      source: 'join_entry',
      ...utmParams,
    }), isApp ? '_system' : '_self');
  }, [
    isApp,
    utmParams,
  ]);

  const cards: JoinEntryCardProps[] = useMemo(() => [
    {
      title: 'Grow your business',
      description: 'Unlock business growth by using our marketing tools to attract new clients.',
      onClick: () => handleClicked(JoinEntryCardType.GrowYourBusiness),
    },
    {
      title: 'Manage your business',
      description: 'Take charge of your business and make booking and scheduling a breeze.',
      onClick: () => handleClicked(JoinEntryCardType.ManageYourBusiness),
    },
    {
      title: 'Elevate your client experience',
      description: 'Prioritize client satisfaction with features that create a seamless booking experience. ',
      onClick: () => handleClicked(JoinEntryCardType.ElevateYourClientExperience),
    },
  ], [handleClicked]);

  return (
    <JoinEntry
      testID={testID}
      cards={cards}
      onClick={() => handleClicked(JoinEntryCardType.ListYourBusiness)}
    />
  );
};
